import * as Constants from 'client/util/Constants';
import { extend, find } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import pluralize from 'pluralize';

const { ItemTypeConsts, ItemTypeDisplayNames } = Constants;
const { groupTypes } = Constants.DirectoryGroupingConsts;
const { device, policy, systemUser } = ItemTypeConsts;

/**
 * The ItemTableIndirectBindingMixin provides methods to handle the unbinding
 * of members (systems, users) from groups (system groups, user groups)
 * that indirectly bind an item (directory, system) to a parent (user, policy).
 *
 * @example
 * <template>
 *   <ItemTable
 *     :handleUnselectLockedItem="handleUnselectLockedItem"
       :handleUnselectMultipleLockedItems="handleUnselectMultipleLockedItems"
 *     :isItemLocked="isItemLocked" />
 * </template>
 * <script>
 * import ItemTable from
 *   'client/components/ItemTable/ItemTable.vue';
 * import ItemTableIndirectBindingMixin from
 *   'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
 *
 * const storeModule = 'SystemCollection';
 *
 * export default {
 *   // ...
 *   components: {
 *     ItemTable,
 *   },
 *
 *   mixins: [
 *     ItemTableIndirectBindingMixin,
 *     ...
 *   ],
 *
 *   // These required fields could be computed, data or props. It really does
 *   // not matter as long as they're present on the component instance.
 *   data() {
 *     return {
 *        groupType: DirectoryGroupingConsts.groupTypes.userGroup,
 *        itemType: ItemTypeConsts.directory,
 *        memberType: DirectoryGroupingConsts.memberTypes.user,
 *        parentType: ItemTypeConsts.systemUser,
 *     }
 *   }
 *
 *   beforeCreate() {
 *     this.$options.methods.handleBeforeCreate(this.$options, storeModule);
 *   },
 * };
 * </script>
 *
 */
export default {
  methods: {
    checkForValidDataAndProps() {
      if (!this.groupType
          || !this.itemType
          || !this.memberType
          || !this.parentType) {
        throw new Error('Required data/props for ItemTableIndirectBindingMixin not present in component');
      }
    },

    getMemberId(item) {
      return this.itemType === systemUser ? item.id : this.getParentId();
    },

    /**
     * @todo The parent may be a Backbone Model when the SystemUserItemTable is
     * used in the SystemDetailsAsidePanel, or it may be the SystemUserModel in
     * the SystemItemTable or the DirectoryItemTable. In the former case the
     * Backbone Model has an id getter that abstracts the fact that the model's
     * id comes from the _id field. The SystemUserModel Vuex store should be
     * modified similarly so that callers don't need to know which property to
     * use!
     */
    getParentId() {
      return this.parent.id || this.parent._id;
    },

    /**
     * The component that uses the mixin should call this method in its
     * beforeCreate hook to ensure that the required getters and actions are
     * dynamically mapped to the Vuex store module it uses. A beforeCreate()
     * hook cannot be implemented here because the ItemTableIndirectBindingMixin
     * does NOT have knowledge of which store module will be used. In addition,
     * the component that mixes it in _might_ not want to use it in certain
     * cases, e.g. when used in a main panel.
     */
    handleBeforeCreate($options, storeModule) {
      extend(
        $options.computed,
        mapGetters(storeModule, {
          isItemIndirectlyBound: 'isItemIndirectlyBound',
          items: 'getCollection',
          getGroupMembership: 'getGroupMembership',
        }),
      );

      extend(
        $options.methods,
        mapActions(storeModule, [
          'removeIndirectBindings',
          'removeMultiIndirectBindings',
        ]),
      );
    },

    unselectUserGroup() {
      return this.parentType === systemUser;
    },

    handleUnselectLockedItem(id) {
      this.checkForValidDataAndProps();

      this.promptToRemoveGroupMembership(id)
        .then((groupIds) => {
          if (groupIds && groupIds.length) {
            const item = find(this.items, item2 => item2.id === id);
            const memberId = this.getMemberId(item);

            this
              .removeIndirectBindings({
                itemId: id,
                groupIds,
                groupType: this.groupType,
                memberId,
                memberType: this.memberType,
                unselectUserGroup: this.unselectUserGroup(),
              })
              .catch(() => {
                notify('error', 'Unable to remove item from groups!');
              });
          }
        });
    },

    handleUnselectMultipleLockedItems(ids) {
      this.checkForValidDataAndProps();

      return this
        .promptToRemoveMultiGroupMemberships()
        .then((canBeRemoved) => {
          if (!canBeRemoved) {
            return false;
          }

          const indirectBindings = ids.map((id) => {
            const item = find(this.items, item2 => item2.id === id);
            const memberId = this.getMemberId(item);
            const groupIds = this.getGroupMembership(id, this.groupType);

            return {
              itemId: id,
              groupIds,
              groupType: this.groupType,
              memberId,
              memberType: this.memberType,
            };
          });

          return this.removeMultiIndirectBindings(indirectBindings, this.unselectUserGroup());
        });
    },

    /**
     * An item is locked when it is indirectly bound to its parent. For example,
     * a directory will be locked when it is bound to a user group that its
     * parent user is a member of.
     */
    isItemLocked(id) {
      return this.isItemIndirectlyBound(id);
    },

    /**
     * Display a modal dialog asking the admin user if they would like to
     * remove a member from a group that provides an indirect binding to the
     * parent, or item.
     *
     * Note that the _user_ will be removed from the user group(s). This is
     * because in the System Details Aside Panel, where the system is the
     * parent and the users are the targets, when unbinding an indirectly bound
     * user, if the system were removed from the system group(s) that provide
     * access to the user then all other users that are members of that user
     * group would lose access to the system; and that is not desirable. That
     * would have side effects on all users who are in the user group that
     * provides indirect access to the system.
     *
     * Similarly, in the User Details Aside Panel if the system were removed
     * from the system group(s) that provide access to the user then all other
     * users that are members of the user group would lose access to that
     * system.
     */
    promptToRemoveGroupMembership(id) {
      // 1) Get group IDs that indirectly bind the target to the parent Or in
      //    the System Aside Panel, that bind the parent to the targets.

      const groupIds = this.getGroupMembership(id, this.groupType);

      // 2) Make an XHR request to get the group names given the group IDs that
      //    provide an indirect binding. Unfortunately the group name is _NOT_
      //    included in the paths array returned from the server.
      // 3) Prompt admin to confirm if they want to remove the member from all
      //    groups that provide access to the target/parent.
      // 4) If the admin confirms that the group membership should be removed
      //    then return the group IDs, which lets the caller know to continue and
      //    remove the parent's group membership from the specified groups.

      return DirectoryGroupingApi
        .getGroupNames(this.groupType, groupIds)
        .then(groups => this
          .displayRemoveGroupMembershipModal(groups)
          .then(canBeRemoved => (canBeRemoved ? groupIds : [])))
        .catch(() => notify('error', 'Unable to get group names!'));
    },

    promptToRemoveMultiGroupMemberships() {
      return this.displayRemoveGroupMembershipModal(undefined, true);
    },

    /**
     * Prompt the admin user to remove the user from the user groups that
     * indirectly bind it to a system.
     */
    displayRemoveGroupMembershipModal(groups = [], multi = false) {
      return new Promise((resolve) => {
        const groupNames = groups.map(group => group.name).join(', ');
        const kbLink = KbArticleUrls.gettingStartedGroups;
        let isDeviceForSystemUser = false;
        const itemTypeName = ItemTypeDisplayNames[this.itemType];

        // Parameterize the text for the dialog based on the parentType and
        // whether multiple items or a single item is being unselected.
        const pronoun = multi ? 'these' : 'this';
        const target = multi ? pluralize(itemTypeName) : itemTypeName;
        const parent = ItemTypeDisplayNames[this.parentType];
        const groupTypeName = this.groupType === groupTypes.systemGroup
          ? ItemTypeDisplayNames[ItemTypeConsts.deviceGroup]
          : ItemTypeDisplayNames[this.groupType];

        let firstSentence;
        let secondSentence;
        let buttonText;

        if (this.parentType === systemUser) {
          firstSentence = `To unbind ${pronoun} ${target} from this ${parent} you will need to remove this ${parent} from all ${groupTypeName}(s) that provide access to ${pronoun} ${target}`;
          secondSentence = `By removing this ${parent} from ${groupTypeName}s you may be removing access to`;
          buttonText = `remove ${parent} from all groups providing access to ${pronoun} ${target}`;
        } else {
          firstSentence = `To unbind ${pronoun} ${target} from this ${parent} you will need to remove ${pronoun} ${target} from all ${groupTypeName}(s) that provide access to this ${parent}`;
          secondSentence = `By removing ${pronoun} ${target} from ${groupTypeName}s you may be removing access to`;
          buttonText = 'remove';
        }

        // Policies tab in Systems main panel
        if (this.itemType === policy) {
          firstSentence = `To unbind this ${parent} from ${pronoun} ${target} you will need to remove this ${parent} from all ${pluralize(`${groupTypeName}`)} that provide access to ${pronoun} ${target}`;
          secondSentence = `By removing ${pronoun} ${parent} from ${groupTypeName} you may be removing access to`;
          buttonText = 'remove';
        }

        // Device tab in User aside panel
        if (this.parentType === systemUser && this.itemType === device) {
          isDeviceForSystemUser = true;
          firstSentence = `Unbinding ${pronoun} ${target.toLowerCase()} from this ${parent.toLowerCase()} will also **remove** this ${parent.toLowerCase()} from all ${pluralize(`${groupTypeName.toLowerCase()}`)} that provide access to ${pronoun} ${target.toLowerCase()}`;
          secondSentence = 'This may also remove access to any connected resources in that group. **Are you sure** you want to continue?';
          buttonText = 'continue';
        }

        // User tab in Device aside panel
        if (this.parentType === device && this.itemType === systemUser) {
          isDeviceForSystemUser = true;
          firstSentence = `Unbinding ${pronoun} ${target.toLowerCase()} from this ${parent.toLowerCase()} will also **remove** ${pronoun} ${target.toLowerCase()} from all ${pluralize(`${groupTypeName.toLowerCase()}`)} that provide access to this ${parent.toLowerCase()}`;
          secondSentence = 'This may also remove access to any connected resources in that group. **Are you sure** you want to continue?';
          buttonText = 'continue';
        }

        // Parmeterize the effected resources based on the group type
        const effectedResources = this.groupType === groupTypes.systemGroup
          ? 'Policies, User Group(s), Software Management and/or Commands'
          : 'Device Group(s), Applications, Radius Servers, and/or Directories';

        const content = `${firstSentence}${multi ? '.' : `: **${groupNames}**.`}

          ${secondSentence}${isDeviceForSystemUser ? '' : `: **${effectedResources}** provided through membership. [Learn more about group bindings](${kbLink}).`}`;

        const componentProps = {
          headerText: `${itemTypeName} Binding`,
          mdTemplate: content,
          continueText: buttonText,
          handleCancel: () => {
            ModalEventBus.close();
            resolve(false);
          },
          handleContinue: () => {
            ModalEventBus.close();
            resolve(true);
          },
          mainActionAttention: true,
          showClose: true,
        };

        if (isDeviceForSystemUser) {
          componentProps.kbLink = kbLink;
          componentProps.kbLinkText = 'Read the Knowledge Base';
        }

        ModalEventBus.open({
          isLargeModal: !isDeviceForSystemUser,
          component: 'ConfirmationModal',
          componentProps,
        });
      });
    },
  },
};
